import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';

const CATEGORIES = ['hangout', 'gaming', 'esports', 'anime', 'music', 'art', 'craft', 'coding', 'education', 'focus', 'irl', 'fitness', 'asmr'];
const PRESET_TAGS = ['chill', 'competitive', 'educational', 'first playthrough', 'speedrun', 'just chatting', 'variety', 'beginner friendly', 'no commentary', 'highlights'];

export default function OBSStreamManager() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [user, setUser] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('gaming');
  const [tags, setTags] = useState([]);
  const [isLive, setIsLive] = useState(false);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    localStorage.setItem('token', token);
    api.me().then(d => { setUser(d.user); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  const handleGoLive = async () => {
    if (!title.trim()) { setStatus('Enter a title'); return; }
    setStatus('');
    try {
      await api.goLive({ title, category, tags });
      setIsLive(true);
      setStatus('You are LIVE!');
    } catch (e) { setStatus(e.message); }
  };

  const handleEndStream = async () => {
    try {
      await api.endStream();
      setIsLive(false);
      setStatus('Stream ended');
    } catch (e) { setStatus(e.message); }
  };

  if (loading) return <div style={{ padding: 16, color: '#adadb8', fontFamily: "'Nunito', sans-serif" }}>Loading...</div>;
  if (!user) return (
    <div style={{ padding: 16, color: '#adadb8', fontFamily: "'Nunito', sans-serif", fontSize: '0.85rem' }}>
      <p>Add your auth token to the URL:</p>
      <code style={{ display: 'block', marginTop: 8, padding: 8, background: '#0e0e10', borderRadius: 4, fontSize: '0.75rem', wordBreak: 'break-all' }}>
        {window.location.origin}/obs/stream-manager?token=YOUR_TOKEN
      </code>
      <p style={{ marginTop: 12 }}>Find your token in Dashboard → copy from browser dev tools (localStorage → token)</p>
    </div>
  );

  return (
    <div style={{ padding: 12, background: '#0e0e10', minHeight: '100vh', fontFamily: "'Nunito', sans-serif", color: '#efeff1' }}>
      <style>{`body { background: #0e0e10 !important; margin: 0; }`}</style>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '0.6rem', color: '#D6E9AA' }}>NINTY</span>
        {isLive && <span style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '0.5rem', color: '#eb0400', animation: 'pixelBlink 1.5s step-start infinite' }}>LIVE</span>}
      </div>

      {/* Title */}
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#adadb8', display: 'block', marginBottom: 4 }}>TITLE</label>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Stream title..."
          style={{ width: '100%', padding: '8px 10px', background: '#18181b', border: '1px solid #2a2a2e', borderRadius: 2, color: '#efeff1', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }}
        />
      </div>

      {/* Category */}
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#adadb8', display: 'block', marginBottom: 4 }}>CATEGORY</label>
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          style={{ width: '100%', padding: '8px 10px', background: '#18181b', border: '1px solid #2a2a2e', borderRadius: 2, color: '#efeff1', fontSize: '0.85rem', outline: 'none', cursor: 'pointer', boxSizing: 'border-box' }}
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
        </select>
      </div>

      {/* Tags */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#adadb8', display: 'block', marginBottom: 4 }}>TAGS ({tags.length}/5)</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {PRESET_TAGS.map(t => {
            const active = tags.includes(t);
            return (
              <button key={t}
                onClick={() => setTags(prev => active ? prev.filter(x => x !== t) : prev.length < 5 ? [...prev, t] : prev)}
                style={{
                  padding: '3px 8px', borderRadius: 2, fontSize: '0.7rem', fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: active ? '#D6E9AA' : '#18181b', color: active ? '#1a1a1a' : '#adadb8',
                  opacity: tags.length >= 5 && !active ? 0.3 : 1,
                }}
              >{t}</button>
            );
          })}
        </div>
      </div>

      {/* Go Live / End */}
      {!isLive ? (
        <button onClick={handleGoLive} style={{
          width: '100%', padding: '10px', background: '#D6E9AA', color: '#1a1a1a', border: 'none',
          borderRadius: 2, fontSize: '0.9rem', fontWeight: 800, cursor: 'pointer',
          boxShadow: '2px 2px 0 rgba(0,0,0,0.3)',
        }}>Go Live</button>
      ) : (
        <button onClick={handleEndStream} style={{
          width: '100%', padding: '10px', background: '#eb0400', color: '#fff', border: 'none',
          borderRadius: 2, fontSize: '0.9rem', fontWeight: 800, cursor: 'pointer',
          boxShadow: '2px 2px 0 rgba(0,0,0,0.3)',
        }}>End Stream</button>
      )}

      {status && <p style={{ fontSize: '0.8rem', marginTop: 8, color: status.includes('LIVE') || status.includes('ended') ? '#00c400' : '#eb0400' }}>{status}</p>}
    </div>
  );
}
